import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Video,
  VideoOff,
  Camera,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  handleEndLive,
  toggleCamera,
  flipCamera,
  toggleMicrophone,
  toggleViewerAudio,
} from "../Utils/agoraUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase/firebase";
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { useAppSelector } from "../hooks/useRedux";
import { usePermissions } from "../hooks/usePermissions";
import AgoraRTC from "agora-rtc-sdk-ng";
import {
  AgoraRTCProvider,
  useRTCClient,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useJoin,
  useRemoteUsers,
  useIsConnected,
  LocalVideoTrack,
  RemoteVideoTrack,
} from "agora-rtc-react";

// const APP_ID = "4055a7c085d741f7a481f03c1dd03f43";
const APP_ID = "d3fb87799daf43c38406f2659a95bf37";
const TOKEN = null;

interface LiveStreamScreenProps {}

const LiveStreamScreen: React.FC<LiveStreamScreenProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isBroadcaster, channelName } = location.state || {
    isBroadcaster: false,
    channelName: "",
  };
  console.log("Data from profile:", { isBroadcaster, channelName });

  const [joined, setJoined] = useState(false);
  const [peerIds, setPeerIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isViewerLoading, setIsViewerLoading] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const permissionsGranted = usePermissions(isBroadcaster);
  const user = useAppSelector((state) => state.user);

  // Timer logic
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (joined) {
      timer = setInterval(() => setElapsedTime((prev) => prev + 1), 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [joined]);

  // Timeout for initialization failure
  useEffect(() => {
    if (isLoading && isBroadcaster) {
      const timeout = setTimeout(() => {
        if (!joined) {
          setError("Failed to initialize live stream. Please try again.");
          setIsLoading(false);
        }
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, isBroadcaster, joined]);

  // Viewer loading timeout
  useEffect(() => {
    if (!isBroadcaster) {
      const timeout = setTimeout(() => {
        setIsViewerLoading(false);
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isBroadcaster]);

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Memoize client to prevent recreation on every render
  const client = useMemo(
    () =>
      AgoraRTC.createClient({
        mode: "live",
        codec: "vp8",
        role: isBroadcaster ? "host" : "audience",
      }),
    [isBroadcaster]
  );

  // Enable AgoraRTC debugging
  useEffect(() => {
    AgoraRTC.setLogLevel(1); // 1 for INFO, 0 for DEBUG
  }, []);

  return (
    <AgoraRTCProvider client={client}>
      <InnerComponent
        isBroadcaster={isBroadcaster}
        channelName={channelName}
        navigate={navigate}
        permissionsGranted={permissionsGranted}
        user={user}
        setJoined={setJoined}
        setPeerIds={setPeerIds}
        setIsLoading={setIsLoading}
        cameraEnabled={cameraEnabled}
        setCameraEnabled={setCameraEnabled}
        micEnabled={micEnabled}
        setMicEnabled={setMicEnabled}
        isAudioMuted={isAudioMuted}
        setIsAudioMuted={setIsAudioMuted}
        joined={joined}
        isLoading={isLoading}
        isViewerLoading={isViewerLoading}
        elapsedTime={elapsedTime}
        formatTime={formatTime}
        peerIds={peerIds}
        setError={setError}
        error={error}
      />
    </AgoraRTCProvider>
  );
};

const InnerComponent = ({
  isBroadcaster,
  channelName,
  navigate,
  permissionsGranted,
  user,
  setJoined,
  setPeerIds,
  setIsLoading,
  cameraEnabled,
  setCameraEnabled,
  micEnabled,
  setMicEnabled,
  isAudioMuted,
  setIsAudioMuted,
  joined,
  isLoading,
  isViewerLoading,
  elapsedTime,
  formatTime,
  peerIds,
  setError,
  error,
}) => {
  const client = useRTCClient();
  const isConnected = useIsConnected();
  const remoteUsers = useRemoteUsers();
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(isBroadcaster);
  const { localCameraTrack, isLoading: isCameraLoading } =
    useLocalCameraTrack(isBroadcaster);

  // Publish tracks only if available
  usePublish([localMicrophoneTrack, localCameraTrack].filter(Boolean));

  // Join channel
  useJoin(
    {
      appid: APP_ID,
      channel: channelName,
      token: TOKEN,
      //   @ts-ignore
      role: isBroadcaster ? "host" : "audience",
    },
    permissionsGranted
  );

  // Log AgoraRTC events
  useEffect(() => {
    console.log("Join status check:", {
      isConnected,
      hasCamera: !!localCameraTrack,
      isCameraLoading,
      channelName,
    });
    console.log(
      "Remote users:",
      remoteUsers.map((u) => ({
        uid: u.uid,
        hasVideo: !!u.videoTrack,
        hasAudio: !!u.audioTrack,
      }))
    );

    const handleUserPublished = async (user, mediaType) => {
      console.log(`User ${user.uid} published ${mediaType}`);
      if (mediaType === "video") {
        await client.subscribe(user, mediaType);
        console.log(`Subscribed to video track for user ${user.uid}`);
      }
    };
    const handleUserJoined = (user) => {
      console.log(`User ${user.uid} joined`);
    };
    const handleUserLeft = (user) => {
      console.log(`User ${user.uid} left`);
    };

    client.on("user-published", handleUserPublished);
    client.on("user-joined", handleUserJoined);
    client.on("user-left", handleUserLeft);

    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-joined", handleUserJoined);
      client.off("user-left", handleUserLeft);
    };
  }, [client, remoteUsers]);

  // Enable/disable tracks
  useEffect(() => {
    if (localMicrophoneTrack) localMicrophoneTrack.setEnabled(micEnabled);
  }, [localMicrophoneTrack, micEnabled]);

  useEffect(() => {
    if (localCameraTrack) localCameraTrack.setEnabled(cameraEnabled);
  }, [localCameraTrack, cameraEnabled]);

  // Join status & Firestore
  useEffect(() => {
    if (isBroadcaster) {
      if (isConnected && localCameraTrack && !isCameraLoading) {
        setJoined(true);
        setIsLoading(false);
        setDoc(doc(db, "live_streams", user.user_id?.toString()), {
          isLive: true,
          channelName,
          startTime: serverTimestamp(),
        }).catch((error) => {
          console.error("Error setting live status:", error);
          setError("Failed to update live status. Please try again.");
        });
      } else if (!permissionsGranted) {
        setIsLoading(false);
      }
    } else {
      setJoined(isConnected);
      setIsLoading(!isConnected);
    }
  }, [
    isConnected,
    isBroadcaster,
    localCameraTrack,
    isCameraLoading,
    channelName,
    user.user_id,
    permissionsGranted,
    setError,
  ]);

  // Update peer IDs
  useEffect(() => {
    setPeerIds(remoteUsers.map((user) => user.uid));
  }, [remoteUsers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isBroadcaster) {
        deleteDoc(doc(db, "live_streams", user.user_id?.toString())).catch(
          (error) => console.error("Error deleting live status:", error)
        );
      }
      client.leave();
    };
  }, [isBroadcaster, user.user_id, client]);

  // Viewer retry logic
  useEffect(() => {
    if (!isBroadcaster && isConnected && remoteUsers.length === 0) {
      const timer = setTimeout(async () => {
        if (remoteUsers.length === 0) {
          console.log("Retrying join for viewer...");
          await client.leave();
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await client.join(APP_ID, channelName, TOKEN, null);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isBroadcaster, isConnected, remoteUsers.length, client, channelName]);

  // Render logic
  if (error) {
    return (
      <div className="h-screen bg-black relative overflow-hidden">
        <span className="text-red-500 text-center text-base mt-5">{error}</span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen bg-black relative overflow-hidden">
        <span className="text-white text-center text-base mt-5">
          Initializing live stream...
        </span>
      </div>
    );
  }

  if (!permissionsGranted && isBroadcaster) {
    return (
      <div className="h-screen bg-black relative overflow-hidden">
        <span className="text-white text-center text-base mt-5">
          Waiting for camera and microphone permissions...
        </span>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black relative overflow-hidden">
      {isBroadcaster && localCameraTrack ? (
        <>
          <div className="absolute top-5 left-1/2 transform -translate-x-1/2 z-20 py-1 px-2 rounded-2xl bg-red-600 bg-opacity-80">
            <span className="text-white text-base font-bold">
              LIVE {formatTime(elapsedTime)}
            </span>
          </div>
          <LocalVideoTrack
            track={localCameraTrack}
            play={true}
            className="w-full h-full z-10 bg-black"
          />
          <div className="absolute bottom-20 w-full z-20 flex flex-row justify-center items-center">
            <div
              className="bg-black bg-opacity-50 rounded-full p-2 mx-2 cursor-pointer"
              onClick={() =>
                handleEndLive(
                  client,
                  navigate,
                  isBroadcaster,
                  user.user_id,
                  localCameraTrack,
                  localMicrophoneTrack
                )
              }
            >
              <X size={24} color="#fff" />
            </div>
            <div
              className="bg-black bg-opacity-50 rounded-full p-2 mx-2 cursor-pointer"
              onClick={() =>
                toggleCamera(localCameraTrack, cameraEnabled, setCameraEnabled)
              }
            >
              {cameraEnabled ? (
                <Video size={24} color="#fff" />
              ) : (
                <VideoOff size={24} color="#fff" />
              )}
            </div>
            <div
              className="bg-black bg-opacity-50 rounded-full p-2 mx-2 cursor-pointer"
              onClick={() =>
                toggleMicrophone(
                  localMicrophoneTrack,
                  micEnabled,
                  setMicEnabled
                )
              }
            >
              {micEnabled ? (
                <Mic size={24} color="#fff" />
              ) : (
                <MicOff size={24} color="#fff" />
              )}
            </div>
          </div>
        </>
      ) : !isBroadcaster && isViewerLoading ? (
        <span className="text-white text-center text-base mt-5">
          Connecting to live stream...
        </span>
      ) : !isBroadcaster && isConnected && remoteUsers.length === 0 ? (
        <span className="text-white text-center text-base mt-5">
          Waiting for broadcaster to join...
        </span>
      ) : !isBroadcaster && remoteUsers.length > 0 ? (
        <>
          <div className="absolute top-5 left-1/2 transform -translate-x-1/2 z-20 py-1 px-2 rounded-2xl bg-gray-500 bg-opacity-80">
            <span className="text-white text-base font-bold">
              Watching {formatTime(elapsedTime)}
            </span>
          </div>
          {remoteUsers.map((user) =>
            user.videoTrack ? (
              <RemoteVideoTrack
                key={user.uid}
                track={user.videoTrack}
                play={true}
                className="w-full h-full z-10 bg-black"
                onError={(err) => {
                  console.error(
                    `Error playing video track for user ${user.uid}:`,
                    err
                  );
                  setError(
                    "Failed to play broadcaster's video. Please try again."
                  );
                }}
              />
            ) : (
              <span
                key={user.uid}
                className="text-white text-center text-base mt-5"
              >
                Broadcaster's video is not available.
              </span>
            )
          )}
          <div className="absolute bottom-20 w-full z-20 flex flex-row justify-center items-center">
            <div
              className="bg-black bg-opacity-50 rounded-full p-2 mx-2 cursor-pointer"
              onClick={() =>
                handleEndLive(
                  client,
                  navigate,
                  isBroadcaster,
                  user.user_id,
                  localCameraTrack,
                  localMicrophoneTrack
                )
              }
            >
              <X size={24} color="#fff" />
            </div>
            <div
              className="bg-black bg-opacity-50 rounded-full p-2 mx-2 cursor-pointer"
              onClick={() =>
                toggleViewerAudio(client, isAudioMuted, setIsAudioMuted)
              }
            >
              {isAudioMuted ? (
                <VolumeX size={24} color="#fff" />
              ) : (
                <Volume2 size={24} color="#fff" />
              )}
            </div>
          </div>
        </>
      ) : (
        <span className="text-white text-center text-base mt-5">
          {isBroadcaster
            ? "Waiting for camera to initialize..."
            : "No broadcaster found. Waiting..."}
        </span>
      )}
    </div>
  );
};

export default LiveStreamScreen;
